// Orchestration hook for the JVVNL global search dropdown. Owns query state,
// type filter, keyboard navigation index, recent searches, and result derivation.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { initializeJvvnlSearchIndex } from "../../../store/state/Jvvnl/SearchIndex";
import {
  searchIndex as runSearch,
  groupResults,
  getPredictiveSuggestions,
  RankedItem,
} from "./searchUtils";
import {
  rewriteNaturalLanguageQuery,
  actionCompletion,
} from "./naturalLanguage";
import { buildEntityLookup } from "./entityLookup";
import {
  buildNavTarget,
  WEATHER_TOKEN_TO_PARAM,
  WEATHER_SUGGEST_WORDS,
  FORECAST_TOKENS,
} from "./deepLink";
import { dateSuggestions, dateCompletion } from "./dateParser";
import {
  addRecentSearch,
  getRecentSearches,
  removeRecentSearch,
} from "./recentSearches";
import {
  getFavorites,
  isFavorite,
  toggleFavorite,
  touchFavorite,
  FavoriteItem,
} from "./favorites";
import { RecentSearch, SearchItem, SearchTypeFilter } from "./types";

export function useJvvnlSearch(isOpen: boolean) {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const index = useSelector(
    (s: any) => s.jvvnlSearchIndex.items as SearchItem[],
  );
  const status = useSelector((s: any) => s.jvvnlSearchIndex.status as string);
  const degraded = useSelector(
    (s: any) => s.jvvnlSearchIndex.degraded as boolean,
  );

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<SearchTypeFilter>("all");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeViewKind, setActiveViewKind] = useState<"Chart" | "Metric">(
    "Chart",
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [recents, setRecents] = useState<RecentSearch[]>([]);
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);

  // Lazy-load the index the first time the search is opened.
  useEffect(() => {
    if (isOpen && status === "idle") {
      dispatch(initializeJvvnlSearchIndex());
    }
    if (isOpen) {
      setRecents(getRecentSearches());
      setFavorites(getFavorites());
    }
  }, [isOpen, status, dispatch]);

  // Full ranked list ("all" filter), computed once and reused for both the
  // filtered `ranked` view and the per-type `counts` (avoids a second O(n) scan
  // per keystroke). Filtering by type post-hoc is equivalent to passing the
  // filter into runSearch, since scoring is independent of the type filter.
  // NLS parse — slot extraction + intent + rewritten query, computed once and
  // reused for results, the action-mode hint, and ghost completion.
  // Entity lookup built ONCE per index load (memoized on the index ref), so the
  // parser can recognize real feeder/plant/substation names regardless of token
  // order. Rebuilt only when the index reference changes.
  const entityLookup = useMemo(() => buildEntityLookup(index), [index]);

  const nls = useMemo(
    () => rewriteNaturalLanguageQuery(query, entityLookup),
    [query, entityLookup],
  );

  // Active parsed date for the pill + to carry on navigation.
  const dateSlot = nls.slots.date ?? null;

  const allRanked = useMemo(() => {
    let results: RankedItem[] = runSearch(nls.rewrittenQuery, index, "all");

    // Location intent ("feeders in jaipur"): keep only the matching entity type
    // and boost rows whose hierarchy mentions the location.
    if (nls.intent === "location" && nls.slots.entityType) {
      const cat =
        nls.slots.entityType === "feeder"
          ? "Feeder"
          : nls.slots.entityType === "plant"
            ? "Plant"
            : null; // substation spans Transco/Discom
      const loc = (nls.slots.location ?? "").toLowerCase();
      results = results.filter((r) => {
        if (r.type !== "entity") return false;
        if (cat) return r.category === cat;
        return /substation/i.test(r.category);
      });
      // Boost hierarchy/location matches to the front (stable otherwise).
      results = results
        .map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const am = (a.r.mapping ?? "").toLowerCase().includes(loc) ? 0 : 1;
          const bm = (b.r.mapping ?? "").toLowerCase().includes(loc) ? 0 : 1;
          return am - bm || a.i - b.i;
        })
        .map((x) => x.r);
    }

    // Entity score boost: when the parser matched real index entities, float
    // those exact rows up (stable) without hard-pinning — they rise within the
    // existing tier sort instead of overriding action/page priority.
    const boostIds = nls.slots.entityIds;
    if (boostIds && boostIds.length) {
      const boost = new Set(boostIds);
      results = results
        .map((r, i) => ({ r, i }))
        .sort((a, b) => {
          const ab = boost.has(a.r.id) ? 0 : 1;
          const bb = boost.has(b.r.id) ? 0 : 1;
          return ab - bb || a.i - b.i;
        })
        .map((x) => x.r);
    }

    if (nls.injected && nls.injected.length) {
      // Injected (action/compare) results rank above normal pages.
      results = [...(nls.injected as RankedItem[]), ...results];
    }
    return results;
  }, [nls, index]);

  // Action Mode flag: surface an "Actions" hint above the results when the parse
  // recognised a command (so the user sees their intent was understood).
  const actionMode = nls.intent === "action";

  const ranked = useMemo(() => {
    if (typeFilter === "favorite") {
      return allRanked.filter((item) =>
        favorites.some((f) => f.id === item.id),
      );
    }
    if (typeFilter === "all") return allRanked;
    return allRanked.filter((item) => item.type === typeFilter);
  }, [allRanked, typeFilter, favorites]);

  const { flat, groups } = useMemo(() => groupResults(ranked), [ranked]);

  // Counts of results dynamically matching the current query per filter type.
  const counts = useMemo(() => {
    const activeQuery = query.trim();
    // When there's no query, runSearch returns [] but the chips should reflect
    // the full index size; fall back to `index` in that case (matches prior).
    const baseList = activeQuery ? allRanked : index;
    let pCount = 0;
    let eCount = 0;
    let mCount = 0;
    for (const item of baseList) {
      if (item.type === "page") pCount++;
      else if (item.type === "entity") eCount++;
      else if (item.type === "metric") mCount++;
    }
    const fCount = activeQuery
      ? baseList.filter((item) => favorites.some((f) => f.id === item.id))
          .length
      : favorites.length;

    return {
      all: baseList.length,
      page: pCount,
      entity: eCount,
      metric: mCount,
      favorite: fCount,
    };
  }, [query, index, allRanked, favorites]);

  // Suggestions: shown once the user has typed ≥2 chars.
  const suggestions = useMemo(() => {
    if (query.trim().length < 2) return [];
    return ranked
      .filter((r) => r.type === "page" || r.type === "metric")
      .slice(0, 4);
  }, [ranked, query]);

  // Predictive suggestion layer — lightweight prefix/substring hits across ALL
  // types, surfaced after only 2–4 chars (command-palette style) in a distinct
  // "Suggestions" section above the full ranked results. Injected action results
  // (already prepended to allRanked) are merged in so commands lead.
  const predictiveSuggestions = useMemo(() => {
    if (query.trim().length < 2) return [];
    const injectedActions = (nls.injected ?? []) as SearchItem[];
    const base = getPredictiveSuggestions(query, index, 6);
    const seen = new Set<string>();
    const out: SearchItem[] = [];
    for (const it of [...injectedActions, ...base]) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      out.push(it);
      if (out.length >= 6) break;
    }
    return out;
  }, [query, index, nls]);

  // Quick-search chips for the empty/home view, built from the REAL index so the
  // entity names actually exist: a feeder + temperature, a substation/circle +
  // forecast, a plain entity, plus a few metric labels. Falls back gracefully
  // when a category is missing from the index.
  const quickSearches = useMemo(() => {
    const out: {
      query: string;
      label: string;
      kind: "weather" | "forecast" | "entity" | "metric" | "ranking";
    }[] = [];
    const byCat = (cat: string) =>
      index.find((it) => it.type === "entity" && it.category === cat);

    const feeder = byCat("Feeder");
    const sub = byCat("Transco Substation") || byCat("Discom Substation");
    const circle = byCat("Circle");
    // Pick a plant with a clean (punctuation-free) label so the example query
    // matches the index reliably — skip names with dots like "pvt.LTD".
    const plant = index.find(
      (it) =>
        it.type === "entity" &&
        it.category === "Plant" &&
        !/[.]/.test(it.label),
    );

    if (feeder)
      out.push({
        query: `${feeder.label} temperature`,
        label: `${feeder.label} temperature`,
        kind: "weather",
      });
    if (sub)
      out.push({
        query: `${sub.label} forecast`,
        label: `${sub.label} forecast`,
        kind: "forecast",
      });
    if (circle)
      out.push({
        query: `${circle.label} humidity`,
        label: `${circle.label} humidity`,
        kind: "weather",
      });
    if (plant)
      out.push({ query: plant.label, label: plant.label, kind: "entity" });

    // Ranking examples (always present — static index): top/bottom feeders.
    out.push({
      query: "top feeders",
      label: "Top Feeders by Load",
      kind: "ranking",
    });
    out.push({
      query: "bottom feeders",
      label: "Bottom Feeders by Load",
      kind: "ranking",
    });
    return out.slice(0, 7);
  }, [index]);

  // ── Autocomplete / suggestions for the weather-pairing flow ──

  // Split the live query into its (optional) weather word + entity remainder so
  // we can suggest the missing half.
  const queryParts = useMemo(() => {
    const norm = query.trim().toLowerCase();
    const tokens = norm.split(/\s+/).filter(Boolean);
    const last = tokens[tokens.length - 1] ?? "";
    let weatherWord: string | null = null;
    let hasForecast = false;
    const rest: string[] = [];
    for (const t of tokens) {
      if (WEATHER_TOKEN_TO_PARAM[t] && !weatherWord) weatherWord = t;
      else if (FORECAST_TOKENS.has(t)) hasForecast = true;
      else rest.push(t);
    }
    return {
      norm,
      tokens,
      last,
      weatherWord,
      hasForecast,
      entityText: rest.join(" "),
    };
  }, [query]);

  // Inline ghost-text completion: when the LAST token is a prefix of exactly one
  // obvious continuation (a weather word, or the top entity result's label),
  // offer the remainder so the user can Tab/→ to accept.
  const completion = useMemo(() => {
    // 0) Action-command completion ("open fo" → "recast"), highest priority so
    //    command intent autocompletes like Raycast/Linear/VS Code.
    const act = actionCompletion(query);
    if (act) return act;

    const { last, weatherWord, entityText } = queryParts;
    if (last.length < 2) return "";
    // Already a complete weather word → no ghost.
    if (WEATHER_TOKEN_TO_PARAM[last]) return "";

    // 1) Completing a weather word or "forecast" (only when an entity is already
    //    typed, or the modifier word is the whole query).
    if (!weatherWord) {
      const cand = [...WEATHER_SUGGEST_WORDS, "forecast"].find(
        (w) => w !== last && w.startsWith(last),
      );
      if (cand && (entityText.length === 0 || entityText !== last)) {
        return cand.slice(last.length);
      }
    }

    // 2) Completing an entity name from the top entity result.
    const topEntity = ranked.find((r) => r.type === "entity");
    if (topEntity) {
      const label = topEntity.label.toLowerCase();
      if (label.startsWith(last) && label !== last) {
        return topEntity.label.slice(last.length);
      }
    }

    // 3) Date-alias completion (lowest priority — entity always wins above).
    const dc = dateCompletion(last);
    if (dc) return dc;

    return "";
  }, [queryParts, ranked, query]);

  // Date suggestion pills: partial last token → matching date labels (≥2 chars).
  const dateSuggestionList = useMemo(() => {
    const last = queryParts.last;
    if (last.length < 2) return [];
    return dateSuggestions(last, new Date());
  }, [queryParts.last]);

  // Weather pills: when the query has matched entities but no weather word yet,
  // offer one-tap weather words to append (turns "badawas" → "badawas temperature").
  const weatherPills = useMemo(() => {
    const { weatherWord, entityText, norm } = queryParts;
    if (weatherWord || norm.length < 2 || entityText.length < 2) return [];
    if (!ranked.some((r) => r.type === "entity")) return [];
    return WEATHER_SUGGEST_WORDS.slice();
  }, [queryParts, ranked]);

  // Forecast pill: shown next to the weather pills when an entity is matched and
  // no forecast token is present yet (turns "badawas" → "badawas forecast").
  const showForecastPill = useMemo(() => {
    const { hasForecast, entityText, norm } = queryParts;
    if (hasForecast || norm.length < 2 || entityText.length < 2) return false;
    return ranked.some((r) => r.type === "entity");
  }, [queryParts, ranked]);

  // Predictive entity views (Part 4): once the NLS parser recognizes a real
  // index entity, surface one-tap "Chart View" / "Metric View" jumps to that
  // entity's result. The matched item is found via the parser's entityIds.
  const entityViews = useMemo(() => {
    const ids = nls.slots.entityIds;
    if (!ids || !ids.length) return null;
    const idSet = new Set(ids);
    const item = index.find((it) => it.type === "entity" && idSet.has(it.id));
    if (!item) return null;
    const hasChart = !!(item.hasDualViews || item.chartTarget || item.deepLink);
    const hasMetric = !!(
      item.hasDualViews ||
      item.metricTarget ||
      item.directQuery
    );
    if (!hasChart && !hasMetric) return null;
    return { item, hasChart, hasMetric };
  }, [nls, index]);

  // Items reachable via keyboard.
  const navigableItems = useMemo(() => {
    const out = [...flat];
    for (const g of groups) {
      if (expanded.has(g.category)) out.push(...g.items);
    }
    return out;
  }, [flat, groups, expanded]);

  // The currently-highlighted item's id.
  const activeId =
    activeIndex >= 0 ? (navigableItems[activeIndex]?.id ?? null) : null;

  // The view kind a freshly-highlighted item should default to: dual-view items
  // start on Chart; single-view items use their own kind (defaulting to Chart).
  const defaultViewKind = (item: SearchItem): "Chart" | "Metric" =>
    item.hasDualViews ? "Chart" : (item.viewKind ?? "Chart");

  // Reset active highlight whenever the result set changes.
  useEffect(() => setActiveIndex(-1), [query, typeFilter]);

  // Synchronize activeViewKind when activeIndex or navigableItems changes.
  useEffect(() => {
    const item = navigableItems[activeIndex];
    if (item) setActiveViewKind(defaultViewKind(item));
  }, [activeIndex, navigableItems]);

  const select = useCallback(
    (
      item: SearchItem,
      targetKind?: "Chart" | "Metric",
    ): { navigated: boolean } => {
      if (item.disabled) return { navigated: false }; // e.g. compare "Coming Soon" groundwork
      setRecents(addRecentSearch(item.label));
      if (isFavorite(item.id)) touchFavorite(item.id); // bump recency
      const target = buildNavTarget(item, targetKind, dateSlot ?? undefined);
      // No-op when the target is the page/query we're already on (e.g. re-picking
      // the filter that's already applied). React Router won't change `location`,
      // so the modal's close-on-location-change effect never fires — report that
      // back so the caller closes the modal itself instead of leaving it open.
      const current = window.location.pathname + window.location.search;
      if (target === current) return { navigated: false };
      navigate(target);
      return { navigated: true };
    },
    [navigate, query, dateSlot],
  );

  const toggleFav = useCallback((item: SearchItem) => {
    setFavorites(toggleFavorite(item));
  }, []);

  const isFav = useCallback(
    (id: string) => favorites.some((f) => f.id === id),
    [favorites],
  );

  const toggleGroup = useCallback((category: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(category) ? next.delete(category) : next.add(category);
      return next;
    });
  }, []);

  const removeRecent = useCallback((q: string) => {
    setRecents(removeRecentSearch(q));
  }, []);

  // Single source of truth for the latest navigable items, read by both
  // setActiveById and the keyboard handler (avoids two parallel refs).
  const navRef = useRef(navigableItems);
  navRef.current = navigableItems;
  const setActiveById = useCallback((id: string) => {
    const idx = navRef.current.findIndex((it) => it.id === id);
    if (idx >= 0) {
      setActiveIndex(idx);
      const item = navRef.current[idx];
      if (item) setActiveViewKind(defaultViewKind(item));
    }
  }, []);

  // Keyboard handler for the input. Returns true when it consumed the event.
  const activeViewRef = useRef(activeViewKind);
  activeViewRef.current = activeViewKind;
  const completionRef = useRef(completion);
  completionRef.current = completion;

  // Append a weather word to the current entity query ("badawas" → "badawas
  // temperature"). Trims any existing weather word first.
  const appendWeather = useCallback((word: string) => {
    setQuery((q) => {
      const tokens = q.trim().split(/\s+/).filter(Boolean);
      const kept = tokens.filter((t) => !WEATHER_TOKEN_TO_PARAM[t]);
      return `${kept.join(" ")} ${word}`.trim();
    });
  }, []);

  // Append "forecast" to the current query (once), preserving any weather word.
  const appendForecast = useCallback(() => {
    setQuery((q) => {
      const tokens = q.trim().split(/\s+/).filter(Boolean);
      if (tokens.some((t) => FORECAST_TOKENS.has(t))) return q;
      return `${q.trim()} forecast`.trim();
    });
  }, []);

  const appendDate = useCallback((label: string) => {
    setQuery((q) => {
      const base = q.trim();
      // Replace a trailing partial token if it's a prefix of the chosen label.
      const toks = base.split(/\s+/).filter(Boolean);
      const last = (toks[toks.length - 1] ?? "").toLowerCase();
      if (last && label.toLowerCase().startsWith(last)) toks.pop();
      return [...toks, label].join(" ");
    });
  }, []);

  const removeDate = useCallback(() => {
    if (!dateSlot) return;
    setQuery((q) => {
      const toks = q.trim().split(/\s+/).filter(Boolean);
      const drop = new Set(dateSlot.matchedTokens);
      // matchedTokens index into the NORMALIZED token list; the raw query is a
      // close-enough match for the common case (single date word/phrase). Strip
      // by value as a robust fallback so the pill always clears.
      const dropVals = new Set(dateSlot.label.toLowerCase().split(/\s+/));
      const kept = toks.filter(
        (t, i) => !drop.has(i) && !dropVals.has(t.toLowerCase()),
      );
      return kept.join(" ");
    });
  }, [dateSlot]);

  // Accept the inline ghost-text completion.
  const acceptCompletion = useCallback(() => {
    if (!completionRef.current) return false;
    setQuery((q) => q + completionRef.current);
    return true;
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): { handled: boolean; close?: boolean } => {
      const items = navRef.current;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (items.length ? (i + 1) % items.length : -1));
        return { handled: true };
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) =>
          items.length ? (i - 1 + items.length) % items.length : -1,
        );
        return { handled: true };
      }
      // Tab / → accept the inline ghost-text completion when one is offered.
      if (
        (e.key === "Tab" || e.key === "ArrowRight") &&
        completionRef.current
      ) {
        e.preventDefault();
        acceptCompletion();
        return { handled: true };
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "Tab") {
        const item = items[activeIndex];
        if (item?.hasDualViews) {
          e.preventDefault();
          setActiveViewKind((k) => (k === "Chart" ? "Metric" : "Chart"));
          return { handled: true };
        }
      }
      if (e.key === "Enter") {
        const pick = items[activeIndex] ?? items[0];
        if (pick) {
          select(pick, pick.hasDualViews ? activeViewRef.current : undefined);
          return { handled: true, close: true };
        }
        return { handled: false };
      }
      if (e.key === "Escape") {
        return { handled: true, close: true };
      }
      return { handled: false };
    },
    [activeIndex, select, acceptCompletion],
  );

  return {
    query,
    setQuery,
    quickSearches,
    actionMode,
    predictiveSuggestions,
    completion,
    acceptCompletion,
    weatherPills,
    showForecastPill,
    entityViews,
    appendWeather,
    appendForecast,
    dateSlot,
    dateSuggestions: dateSuggestionList,
    appendDate,
    removeDate,
    typeFilter,
    setTypeFilter,
    activeIndex,
    activeId,
    activeViewKind,
    setActiveViewKind,
    setActiveById,
    flat,
    groups,
    expanded,
    toggleGroup,
    navigableItems,
    recents,
    removeRecent,
    favorites,
    toggleFav,
    isFav,
    suggestions,
    select,
    onKeyDown,
    status,
    degraded,
    counts,
    hasQuery: query.trim().length > 0,
  };
}
